/**
 * Runtime configuration.
 *
 * `public/runtime-config.js` is loaded by index.html BEFORE the app bundle, so
 * every value here is available the moment the SPA mounts. In a container the
 * file is regenerated from the environment at boot
 * (scripts/generate-runtime-config.sh), which means **one build artefact can be
 * promoted through dev → staging → prod without rebuilding**. Build-time
 * `VITE_*` vars are only the fallback.
 *
 * Add a key here → add it to the RuntimeConfig type → add it to the generator
 * script's heredoc. All three, or it will silently be undefined in prod.
 */

export type Density = 'compact' | 'default' | 'comfortable'
export type ThemeChoice = 'light' | 'dark' | 'system'

export interface RuntimeConfig {
  /** Base origin of the Axis API — no trailing slash, no `/api/v1`. */
  apiUrl: string
  /** Shown in the top bar, the login hero and the document title. */
  appName: string
  /** Sub-line under the product name on the login hero. */
  appTagline: string
  /** One letter for the brand mark. */
  brandMark: string
  /**
   * Environment label. `production` hides the badge entirely — a badge that is
   * always on stops being a signal.
   */
  environment: 'local' | 'development' | 'staging' | 'production'
  /** Build/release identifier surfaced in the status bar and the login footer. */
  release: string
  /** Defaults, before the user has expressed a preference. */
  defaultDensity: Density
  defaultTheme: ThemeChoice
  /**
   * Mapbox public access token (`pk.…`).
   *
   * **Deliberately empty in DEFAULTS.** A token is a per-deployment value, and a
   * fallback baked into the bundle would mean staging quietly billing the dev
   * account and a rotation requiring a rebuild. Empty is a supported state: every
   * map degrades to the built-in vector preview, which needs no network and no
   * account (see `shared/map/UnitMap`).
   *
   * The token is public by design — it ships to the browser and cannot be kept
   * secret. What makes it safe is **URL restriction**, set on the token in the
   * Mapbox dashboard, not secrecy. Restrict it to the deployment's origins.
   */
  mapboxToken: string
  /** Style URLs, so a client can point at their own brand styles. */
  mapboxStyleLight: string
  mapboxStyleDark: string
  mapboxStyleStreets: string
  mapboxStyleSatellite: string
  /** Feature flags — hide modules that are not built yet, per deployment. */
  features: {
    /**
     * Show not-yet-built modules in the rail, dimmed, as a roadmap hint.
     * OFF by default: the navigation should only offer what the API actually
     * serves. A menu full of dead entries is clutter, and it makes the product
     * look broken rather than in-progress. Turn on for a roadmap demo.
     */
    roadmapNav: boolean
    /**
     * Geographic scope picker. Renders a read-only NATIONAL pill until Phase 2
     * `geography` ships the unit tree behind it.
     */
    scopePicker: boolean
    /** Show the KPI strip on list screens by default. */
    kpiStrip: boolean
    /** ⌘K command palette. */
    commandPalette: boolean
  }
  /** Login-page content. Copy lives in config so it can be tuned per client. */
  login: {
    headline: string
    body: string
    /** Capability bullets on the hero panel. `icon` keys into HERO_ICONS. */
    features: { icon: string; title: string; desc: string }[]
    /** Compliance/assurance strip along the hero footer. */
    badges: { icon: string; label: string }[]
    /** Shown under the form — where to go when you cannot get in. */
    supportEmail: string
  }
}

declare global {
  interface Window {
    __AXIS_RUNTIME_CONFIG__?: Partial<RuntimeConfig>
  }
}

const DEFAULTS: RuntimeConfig = {
  apiUrl: (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:58090',
  appName: 'Crowbyt Axis',
  appTagline: 'Elections Intelligence Platform',
  brandMark: 'A',
  environment: 'local',
  release: 'dev',
  defaultDensity: 'compact',
  defaultTheme: 'system',
  mapboxToken: '',
  mapboxStyleLight: 'mapbox://styles/mapbox/light-v11',
  mapboxStyleDark: 'mapbox://styles/mapbox/dark-v11',
  mapboxStyleStreets: 'mapbox://styles/mapbox/streets-v12',
  mapboxStyleSatellite: 'mapbox://styles/mapbox/satellite-streets-v12',
  features: {
    roadmapNav: false,
    scopePicker: true,
    kpiStrip: true,
    commandPalette: true,
  },
  login: {
    headline: 'Run the whole campaign from one command centre.',
    body:
      'From ward-level mobilisation to national tallying, voter relocation, field intelligence and finance — Axis unifies every workflow your team needs, on one auditable record.',
    features: [
      {
        icon: 'map',
        title: 'Electoral units',
        desc: 'Country to polling station — 47 counties, 290 constituencies, 1,450 wards, ~46,000 stations.',
      },
      {
        icon: 'users',
        title: 'Voter registry & relocation',
        desc: 'Encrypted register, adopt-a-station coverage, and reviewable relocation proposals with a full evidence trail.',
      },
      {
        icon: 'scan',
        title: 'Form 34A/B/C capture',
        desc: 'On-device OCR with per-field confidence and human review. Nothing is ever auto-committed.',
      },
      {
        icon: 'shield',
        title: 'Auditable by design',
        desc: 'Every change and every access attempt recorded in an append-only trail the application cannot alter.',
      },
    ],
    badges: [
      { icon: 'lock', label: 'DPA 2019 compliant' },
      { icon: 'shield', label: 'Encrypted at rest' },
      { icon: 'pin', label: 'Hosted in Kenya' },
    ],
    supportEmail: 'support@crowbyt.com',
  },
}

function merge(base: RuntimeConfig, override?: Partial<RuntimeConfig>): RuntimeConfig {
  if (!override) return base
  return {
    ...base,
    ...override,
    features: { ...base.features, ...(override.features ?? {}) },
    login: { ...base.login, ...(override.login ?? {}) },
  }
}

export const config: RuntimeConfig = merge(DEFAULTS, window.__AXIS_RUNTIME_CONFIG__)

/** `/api/v1` prefixed base. Everything in `shared/api` goes through this. */
export const apiBase = `${config.apiUrl.replace(/\/+$/, '')}/api/v1`

/** Production hides the badge — an always-on badge stops being a signal. */
export const showEnvBadge = config.environment !== 'production'
